import React from 'react';
import { Link } from 'react-router-dom';
import './ArticlePage.css';

function MoonPhasesMood() {
  return (
    <div className="article-page">
      <div className="article-container">
        <header className="article-header">
          <h1>How Moon Phases Affect Your Mood</h1>
          <Link to="/" className="back-link">← Back to Daily Horoscopes</Link>
        </header>
        <article className="article-content">
          <h2>Introduction: The Moon and Your Emotional World</h2>
          <p>
            Many people report that their mood, energy, and even sleep change with the phases of the
            Moon. From the dark New Moon to the bright Full Moon and back, the lunar cycle has been
            linked for centuries to emotions, intuition, and behaviour. This article explores how Moon
            phases are thought to affect your mood, what science and tradition say, and how you can
            use that awareness to support your emotional well-being—whether you see it as astrology,
            biology, or simply a useful rhythm for self-care.
          </p>
          <p>
            The Moon’s cycle lasts about 29.5 days. In that time it moves through eight main phases:
            New Moon, Waxing Crescent, First Quarter, Waxing Gibbous, Full Moon, Waning Gibbous, Last
            Quarter, and Waning Crescent. Each phase is associated with different qualities in
            astrological and folk tradition: beginnings, growth, peak, release, and rest. Even if you
            don’t believe the Moon “causes” mood changes, paying attention to the phase can help you
            name what you feel and plan your days accordingly—more action when the Moon is waxing,
            more reflection when it’s waning.
          </p>

          <h2>The New Moon: Low Energy and New Beginnings</h2>
          <p>
            At the New Moon, the Moon sits between the Earth and the Sun, so the side we see is
            unlit. In astrology and many cultures, this phase is linked to new beginnings, planting
            seeds, and setting intentions. Mood-wise, people often describe feeling more inward,
            quiet, or even low-energy around the New Moon. Some feel a sense of possibility—a
            clean slate—while others feel tired or withdrawn. That dip in energy can be a signal
            to rest rather than push.
          </p>
          <p>
            If you notice you’re more reflective or low around the New Moon, you’re not alone. Using
            this phase for rest, journaling, and intention-setting can turn that inward pull into
            something constructive. Instead of fighting the low energy, you might schedule fewer
            commitments and use the time to clarify what you want in the coming weeks. The New Moon
            doesn’t have to make you sad; it can be a monthly pause for reset and reflection.
          </p>

          <h2>The Waxing Moon: Building Energy and Motivation</h2>
          <p>
            From New Moon to Full Moon, the visible part of the Moon grows each night. This “waxing”
            half of the cycle is often associated with building energy, motivation, and outward
            action. Many people report feeling more energetic, social, or productive as the Moon
            waxes. If your mood tends to lift during this time, it can be a good period to start
            projects, have important conversations, or step up your exercise and social life.
          </p>
          <p>
            Not everyone feels the same way—some people feel more anxious or restless as the Moon
            grows. The key is to notice your own pattern. If you know you typically feel more
            driven in the waxing phase, you can plan important tasks and meetings then. If you
            feel more scattered or irritable, you might focus on channeling that energy into
            physical activity or creative work rather than into conflict or overcommitment.
          </p>

          <h2>The Full Moon: Peak Emotion and Clarity</h2>
          <p>
            The Full Moon is the most talked-about phase when it comes to mood. The lunar disc is
            fully lit, and traditions worldwide link it to heightened emotion, clarity, revelation,
            and sometimes restlessness or insomnia. Many people report feeling more emotional,
            sensitive, or unable to sleep around the Full Moon. Others feel a burst of creativity
            or a need to bring something to light—a truth, a project, or a feeling they’ve been
            holding back.
          </p>
          <p>
            Science has not consistently proven that the Full Moon causes behavioural or mood
            changes, but the experience is real for many. If you know you’re more reactive or
            emotional at the Full Moon, you can plan for it: get extra rest, avoid scheduling
            difficult conversations if possible, and use the energy for reflection or creative
            expression rather than impulsive decisions. The Full Moon can be a time of clarity—when
            feelings and situations become obvious—so use that clarity wisely instead of letting
            it tip into overwhelm.
          </p>

          <h2>The Waning Moon: Release and Winding Down</h2>
          <p>
            After the Full Moon, the visible part of the Moon shrinks. This “waning” phase is often
            linked to release, letting go, and integration. Mood-wise, people may feel more
            reflective, tired, or ready to finish things and clear space. It’s a good time for
            decluttering, forgiving, and tying up loose ends rather than starting new ventures.
          </p>
          <p>
            If you feel lower or more inward during the waning Moon, that’s a common pattern. You
            can lean into it by scheduling less, prioritising rest, and doing gentle activities
            like walking, reading, or meditation. The waning phase doesn’t have to be depressing; it
            can be a natural time to slow down and recover before the next New Moon and a new
            cycle begins.
          </p>

          <h2>Why Might the Moon Affect Mood?</h2>
          <p>
            There are several ways to explain the link between Moon phases and mood. Astrologically,
            the Moon rules emotions, habits, and the unconscious; its phase is thought to influence
            how we feel and react. Biologically, the Moon affects tides through gravity; some
            researchers have wondered whether it could subtly affect fluids in the body or
            circadian rhythms, though evidence is mixed and often controversial. Psychologically,
            belief and expectation play a role: if you’re told the Full Moon makes people emotional,
            you may notice and remember those nights more. Culture and ritual also matter: many
            people use Moon phases for intention-setting or release, which can improve mood through
            a sense of meaning and structure.
          </p>
          <p>
            You don’t have to choose one explanation. What matters is whether tracking the Moon
            helps you. If you notice that your mood or sleep shifts with the phase, you can use that
            awareness to be kinder to yourself and to plan your life in a way that honours your
            natural rhythm. That’s a practical benefit regardless of cause.
          </p>

          <h2>Using Moon Phases for Better Mood and Self-Care</h2>
          <p>
            You can use the lunar cycle as a framework for emotional self-care. At the New Moon,
            set intentions and allow yourself to rest. During the waxing phase, take action on
            those intentions and lean into your energy. At the Full Moon, acknowledge what’s come
            up—emotions, truths, projects—and decide what to release or celebrate. During the waning
            phase, wind down, forgive, and prepare for the next cycle. You don’t need to believe in
            astrology to benefit from this rhythm; it’s a simple way to structure the month and
            give your mood a narrative that reduces self-blame (“Why do I feel so low?”) and
            increases agency (“It’s a waning phase; I’ll focus on rest”).
          </p>
          <p>
            If you struggle with mood disorders, Moon phase awareness is not a substitute for
            professional care. But it can complement therapy and medication by helping you notice
            patterns and plan around harder times. The goal is to work with your experience, not to
            let a calendar dictate how you should feel. When the Moon affects your mood, knowing
            the phase can make you feel less alone and more in control—and that alone can improve
            how you feel.
          </p>

          <h2>Tracking Your Own Moon–Mood Pattern</h2>
          <p>
            If you want to see how Moon phases affect your mood, try keeping a simple log for a
            few months: note the date, the Moon phase (you can find this on any lunar calendar),
            and a few words about your energy and mood. Over time, you may notice that you feel
            more tired at the New Moon, more wired at the Full Moon, or more reflective during the
            waning phase. That data is personal and doesn’t need to match anyone else’s. Once you
            see your pattern, you can plan your month around it—scheduling demanding tasks when
            you’re usually more energetic and leaving space for rest when you’re typically lower.
            The Moon then becomes a practical tool for self-care, not just a symbol.
          </p>

          <h2>Conclusion: Your Mood and the Moon</h2>
          <p>
            Moon phases and mood have been connected in tradition and in many people’s lived
            experience. Whether the link is astrological, biological, or psychological, you can
            use it to support your well-being: notice when you feel more energetic or more
            reflective, and align your schedule and self-care with the cycle when it helps. The
            Moon doesn’t have to rule you—it can simply offer a rhythm that makes your emotional
            life a little more understandable and a little easier to navigate.
          </p>
        </article>
      </div>
    </div>
  );
}

export default MoonPhasesMood;
